import { ref, computed, type Ref } from '@/composition/vue/compositionApi'
import { useAsyncFuncStatus } from '@/composition/useAsyncFuncStatus'
import type { UsePaginationOptions, UsePaginationProviderResponse } from '@/composition/types'
import { TmCancelRequestError } from '@/core/error/transport/tmCancelRequestError'
import { handleFormError } from '@/composition/forms'

export const usePagination = <TEntity>(options: UsePaginationOptions<TEntity>) => {
  const items = ref<TEntity[]>([]) as Ref<TEntity[]>
  const pagination = ref<UsePaginationProviderResponse<TEntity>['pagination'] | null>(null)
  const requestedPage = computed(() => (pagination.value?.page ?? 0) + 1)
  // @todo it is better to implement filtering in the BE, because there may be many requests to the api
  const isSuccessLoadInitialItems = ref(false)
  const isFirstPage = computed(() => requestedPage.value === 1)
  const isLastPage = computed(() => pagination.value && pagination.value.page === pagination.value.pageCount)

  // lastStartedAt is used to prevent showing the result of the old request after the new one (PR CAR-4333)
  let lastStartAt: number
  const { isPending: isLoading, run } = useAsyncFuncStatus(async () => {
    const startAt = Date.now()
    lastStartAt = startAt
    const res = await options.provider({
      page: requestedPage.value,
      perPage: options.perPage ?? 25,
    })
    if (lastStartAt !== startAt) return
    res.items.forEach((t) => {
      items.value.push(t)
    })
    pagination.value = {
      page: res.pagination.page,
      pageCount: res.pagination.pageCount,
    }
    if (res.items.length === 0 && !isLastPage.value) {
      await loadPage()
    } else {
      isSuccessLoadInitialItems.value = true
    }
  })

  const loadPage = async () => {
    try {
      await run()
    } catch (e) {
      if (e instanceof TmCancelRequestError) {
        return
      }
      handleFormError(e)
    }
  }

  const isLoadingFirstPage = computed(() => {
    // console.log('isLoading.value', isLoading.value)
    return isLoading.value && (isFirstPage.value || !isSuccessLoadInitialItems.value)
  })
  const isLoadingMorePage = computed(() => isLoading.value && !isFirstPage.value)

  const reset = () => {
    isSuccessLoadInitialItems.value = false
    items.value = []
    pagination.value = null
  }

  const loadMoreIfAllowed = async () => {
    if (isLoading.value || isLastPage.value) {
      return
    }
    await loadPage()
  }

  const loadFirstPage = () => {
    reset()
    return loadPage()
  }

  return {
    items,
    isLoadingFirstPage,
    isLoadingMorePage,
    loadMoreIfAllowed,
    loadFirstPage,
    reset,
  }
}
